import { Button, Card, CardContent, Grid } from "@mui/material";
import { FormAutoCompleteTableSelect } from "../../../components/hook-form/form-autocomplete-table-select.dynamic.component";
import FormInput from "../../query-builder/hooks-form-querybuilder/form-input.component";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useAuthorize } from "../../../hooks/use-authorize";
import { UserPermissionName } from "../../../models/user-permission-name";
import { projectsApiKeyStore } from "../../../api/projects-api-key-store";
import { useLoadData } from "../../../hooks/useLoadData";
import { ApiResponse } from "../../../models/api-response";
import { GuardedComponent } from "../../../components/guarded-component";

interface OfficeServiceNewPriceFields {
    officeID: any;
    refetchPricing?: boolean;
    onPriceSelect: (pricingData: any) => void;
    openNewPriceDialog: () => void;
    openEditPriceDialog: (pricingId: any) => void;
}

export const OfficeServiceNewPriceFields = ({ officeID, refetchPricing, onPriceSelect, openNewPriceDialog, openEditPriceDialog }: OfficeServiceNewPriceFields) => {

    const { setValue, getValues } = useFormContext();
    const [selectedPrice, setSelectedPrice] = useState<any>(null);
    const [pricingList, setPricingList] = useState<any>([]);

    const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(projectsApiKeyStore.getProjectPricingListByOffice(officeID));

    const pricingOptionMap: Record<any, string> = {
        AllNewStarts: 'All New Starts',
        AllLots: 'All Lots',
        AllNewTH: 'All New TH',
        AllNewSFR: 'All New SFR',
        AllNewCM: 'All New Commercial (CM)'
    };

    useEffect(() => {
        refetch();
    }, [refetchPricing])

    const showUpdatedPricing = (updatedPrice: any) => {
        setSelectedPrice(updatedPrice);
        onPriceSelect(updatedPrice);
        setValue('officePrice', { pricingListID: updatedPrice?.pricingListID ?? '', pricingListName: updatedPrice?.pricingListName ?? "" });
        setValue('priceStartDate', updatedPrice?.pricingStartDate ?? '');
        setValue('priceEndDate', updatedPrice?.pricingEndDate ?? '');
        setValue('priceApplyTo', updatedPrice?.pricingListAppliesToLotTypeLabel ?? '');
    }

    useEffect(() => {
        if (data) {
            const updatedPriceData = data?.data?.map(item => ({
                ...item,
                pricingListAppliesToLotTypeLabel:
                    pricingOptionMap[item.pricingListAppliesToLotType] || null,
            }));
            if (updatedPriceData?.length > 0) {
                const setPrice = updatedPriceData?.find((item: any) => item?.pricingListID === selectedPrice?.pricingListID)
                showUpdatedPricing(setPrice);
            }
            if (getValues('pricingListID')) {
                const setPrice = updatedPriceData?.find((item: any) => item?.pricingListID === getValues('pricingListID'))
                showUpdatedPricing(setPrice);
            }
            setPricingList(updatedPriceData);
        }
    }, [data])

    const handlePriceChange = (row: any) => {
        setSelectedPrice(row);
        onPriceSelect(row);
        if (row) {
            setValue('priceStartDate', row?.pricingStartDate ?? '');
            setValue('priceEndDate', row?.pricingEndDate ?? '');
            setValue('priceApplyTo', row?.pricingListAppliesToLotTypeLabel ?? '');
        }
        else {
            setValue('priceStartDate', "");
            setValue('priceEndDate', "");
            setValue('priceApplyTo', "");
        }
    };

    const handleOpenEditPrice = (pricingListID: any) => {
        openEditPriceDialog(pricingListID);
    }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={2}>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                    <FormAutoCompleteTableSelect
                        name="officePrice"
                        label="Price List Name"
                        dataItems={pricingList}
                        onChange={handlePriceChange}
                        idKey="pricingListID"
                        optionWidth={'900px'}
                        isParentLoading={isLoading || isFetching}
                        getOptionLabel={(o) => o.pricingListName}
                        columns={[
                            { header: "Pricing List Name", field: "pricingListName", width: "2fr" },
                            { header: "Start Date", field: "pricingStartDate", width: "1fr" },
                            { header: "End Date", field: "pricingEndDate", width: "1fr" },
                            { header: "Pricing List Applies To", field: "pricingListAppliesToLotTypeLabel", width: "2fr" }
                        ]}
                        optionsValueKey="pricingListID"
                    />
                </Grid>
                <Grid item lg={1.1} sm={1.1} xl={1.1} xs={1.1}>
                    <FormInput name="priceStartDate" label="Pricing Start Date" disabled />
                </Grid>
                <Grid item lg={1.1} sm={1.1} xl={1.1} xs={1.1}>
                    <FormInput name="priceEndDate" label="Pricing End Date" disabled />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                    <FormInput name="priceApplyTo" label="Pricing List Applies To" disabled />
                </Grid>
                <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_PRICING_LIST]}>
                    <Grid item lg={1.2} sm={1.2} xl={1.2} xs={1.2}>
                        <Button
                            type={'button'}
                            variant="contained"
                            fullWidth
                            onClick={() => handleOpenEditPrice(selectedPrice?.pricingListID)}
                            disabled={selectedPrice == null || isReadOnlyPermission}
                        >
                            Edit Price List
                        </Button>
                    </Grid>
                </GuardedComponent>
                <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PRICING_LIST]}>
                    <Grid item lg={1.2} sm={1.2} xl={1.2} xs={1.2}>
                        <Button
                            type={'button'}
                            fullWidth
                            variant="contained"
                            onClick={openNewPriceDialog}
                            disabled={isReadOnlyPermission}
                        >
                            Add New Price List
                        </Button>
                    </Grid>
                </GuardedComponent>
            </Grid>
        </Grid>
    );
}